import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, 
  CreditCard, 
  CheckCircle2, 
  AlertCircle, 
  Lightbulb, 
  UserCheck, 
  Building2, 
  Wallet, 
  Shield, 
  ExternalLink,
  Clock,
  Mail,
  Globe,
  FileText,
  ArrowRight,
  Users,
  Settings,
  Calendar,
  MapPin,
  Ticket,
  Target,
  TrendingUp,
  PiggyBank,
  Gift,
  Dices,
  Trophy,
  BarChart3,
  Download,
  PieChart,
  Receipt,
  BookOpen,
  Play,
  Eye,
  Lock,
  Plus
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import mollieLogo from "@/assets/mollie-logo.png";

interface ArticleContent {
  title: string;
  category: string;
  readTime: string;
  content: React.ReactNode;
}

const articles: Record<string, ArticleContent> = {
  "getting-started-with-mollie": {
    title: "Getting Started with Mollie Payments",
    category: "Payments",
    readTime: "10 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <img src={mollieLogo} alt="Mollie" className="h-8 w-auto" />
            <Badge variant="secondary">European Payment Provider</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Mollie is a trusted European payment provider that allows your PTA to accept payments via iDEAL, credit cards, Apple Pay, and more. 
            This guide will walk you through the complete setup process after clicking "Connect Mollie" in PTA Hub.
          </p>
        </div>

        {/* Benefits Section */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Why Choose Mollie?
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Shield className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">GDPR Compliant</p>
                  <p className="text-sm text-muted-foreground">Fully compliant with European data protection regulations</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Wallet className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">No Monthly Fees</p>
                  <p className="text-sm text-muted-foreground">Only pay when you receive payments</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <CreditCard className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Transparent Pricing</p>
                  <p className="text-sm text-muted-foreground">1.8% - 3.25% + small fixed fee per transaction</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              1
            </div>
            <h2 className="text-xl font-bold">Click Connect Mollie in PTA Hub</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>To start the connection process:</p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Navigate to <strong className="text-foreground">PTA Admin</strong> in the main menu</li>
                <li>Click on the <strong className="text-foreground">Payments</strong> tab</li>
                <li>Find the Mollie card and click <strong className="text-foreground">"Connect Mollie"</strong></li>
                <li>You'll be redirected to Mollie's website to complete the setup</li>
              </ol>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> Have your organisation's details ready, including your charity number (if applicable) and bank account information.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 2 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              2
            </div>
            <h2 className="text-xl font-bold">Create Your Mollie Account</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>When you arrive at Mollie's website, you'll need to create an account:</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Mail className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Enter Your Email Address</p>
                    <p className="text-sm text-muted-foreground">Use an official PTA email address if possible (e.g., treasurer@yourpta.org)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Verify Your Email</p>
                    <p className="text-sm text-muted-foreground">Check your inbox for a verification code and enter it on the Mollie website</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Building2 className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Enter Organisation Details</p>
                    <p className="text-sm text-muted-foreground">Provide your PTA's name, address, and basic information</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 3 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              3
            </div>
            <h2 className="text-xl font-bold">Complete the Onboarding Process</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Mollie will ask for additional information about your organisation:</p>
              
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-primary" />
                    Stakeholder/Owner Information
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Enter details of the person(s) who have significant control over the organisation. 
                    For PTAs, this is typically the Chair, Treasurer, or Secretary.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Business Activity
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    When asked about your type of organisation, select:
                  </p>
                  <Badge variant="outline" className="text-sm">"Association / Club / Society"</Badge>
                  <p className="text-sm text-muted-foreground mt-2">
                    For business activity, describe your fundraising activities (e.g., "School fundraising events, merchandise sales, event tickets")
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Choose Payment Methods
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Select the payment methods you want to offer. We recommend enabling:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="secondary">Credit/Debit Cards</Badge>
                    <Badge variant="secondary">Apple Pay</Badge>
                    <Badge variant="secondary">Google Pay</Badge>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Statement of Intent
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Confirm your intended business start date and expected transaction volume. 
                    For most PTAs, a low to medium volume is appropriate.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 4 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              4
            </div>
            <h2 className="text-xl font-bold">Verify Your Identity</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>
                For security and regulatory compliance, Mollie requires identity verification 
                for at least one representative of your organisation.
              </p>
              
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-sm">Accepted Documents</p>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Valid Passport</li>
                    <li>• National ID Card</li>
                    <li>• UK or Netherlands Driving Licence</li>
                  </ul>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                You can upload a photo or scan of your document directly on the Mollie website. 
                Make sure all details are clearly visible and the document is not expired.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Step 5 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              5
            </div>
            <h2 className="text-xl font-bold">Link Your Bank Account</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Connect your PTA's bank account to receive payouts:</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">1</div>
                  <div>
                    <p className="font-medium">Enter Bank Account Details</p>
                    <p className="text-sm text-muted-foreground">Provide your IBAN or UK bank account number and sort code</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">2</div>
                  <div>
                    <p className="font-medium">Make a Verification Payment</p>
                    <p className="text-sm text-muted-foreground">Mollie may ask you to make a small payment (usually 1 penny) to verify the account</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">3</div>
                  <div>
                    <p className="font-medium">Wait for Verification</p>
                    <p className="text-sm text-muted-foreground">Bank verification usually takes 1-3 business days</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Verification typically completes within 1-3 business days</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 6 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
              6
            </div>
            <h2 className="text-xl font-bold">Authorize PTA Hub Connection</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Once your account is verified, you can authorize PTA Hub to connect:</p>
              
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Mollie will show you a permissions screen</li>
                <li>Review the permissions PTA Hub is requesting (read payments, create payments, etc.)</li>
                <li>Click <strong className="text-foreground">"Grant Access"</strong> to authorize the connection</li>
                <li>You'll be redirected back to PTA Hub automatically</li>
              </ol>

              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-sm">Success!</p>
                  <p className="text-sm text-muted-foreground">
                    Once authorized, you'll see "Connected" in the Mollie card on your Payments tab. 
                    You can now start accepting payments!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tips Section */}
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Tips & Troubleshooting
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Verification taking too long?</p>
                  <p className="text-sm text-muted-foreground">
                    Check your email for any requests from Mollie for additional information. 
                    You can also log in to your Mollie dashboard to check the status.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Document rejected?</p>
                  <p className="text-sm text-muted-foreground">
                    Ensure your ID photo is clear, well-lit, and shows all four corners of the document. 
                    Try taking a new photo in good lighting.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Need help?</p>
                  <p className="text-sm text-muted-foreground">
                    Contact Mollie's support team directly through their dashboard, or submit a support 
                    ticket in PTA Hub if you need assistance with the connection.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <h4 className="font-medium mb-3">Mollie Support</h4>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://help.mollie.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Globe className="h-4 w-4" />
                  help.mollie.com
                  <ExternalLink className="h-3 w-3" />
                </a>
                <a 
                  href="mailto:info@mollie.com" 
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  info@mollie.com
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
  },

  "getting-started-with-pta-hub": {
    title: "Getting Started with PTA Hub",
    category: "Getting Started",
    readTime: "8 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <BookOpen className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Quick Start Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Welcome to PTA Hub! This guide will walk you through setting up your PTA account, 
            inviting your committee members, and getting ready to manage your school's PTA effectively.
          </p>
        </div>

        {/* What You'll Learn */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              What You'll Learn
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Settings className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Initial Setup</p>
                  <p className="text-sm text-muted-foreground">Configure your PTA's basic settings</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Users className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Invite Members</p>
                  <p className="text-sm text-muted-foreground">Add your committee to the platform</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Calendar className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Create Events</p>
                  <p className="text-sm text-muted-foreground">Plan and manage your first event</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Target className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Set Goals</p>
                  <p className="text-sm text-muted-foreground">Track your fundraising targets</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Complete Your PTA Profile</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Start by setting up your PTA's profile with essential information:</p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Navigate to <strong className="text-foreground">PTA Admin</strong> from the main menu</li>
                <li>Click on the <strong className="text-foreground">Settings</strong> tab</li>
                <li>Enter your school name, PTA name, and contact details</li>
                <li>Upload your PTA logo if you have one</li>
                <li>Set your charity number (if registered)</li>
              </ol>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> A complete profile helps parents identify your PTA and builds trust.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 2 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Invite Your Committee Members</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Add your fellow committee members so they can access the platform:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">1</div>
                  <div>
                    <p className="font-medium">Go to Members Page</p>
                    <p className="text-sm text-muted-foreground">Navigate to Members from the main menu</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">2</div>
                  <div>
                    <p className="font-medium">Click Invite Member</p>
                    <p className="text-sm text-muted-foreground">Enter their email address and select their role</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">3</div>
                  <div>
                    <p className="font-medium">They'll Receive an Invitation</p>
                    <p className="text-sm text-muted-foreground">Members will get an email to create their account and join</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 3 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">3</div>
            <h2 className="text-xl font-bold">Explore the Dashboard</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Familiarise yourself with the key areas of PTA Hub:</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Dashboard</p>
                    <p className="text-sm text-muted-foreground">Overview of your PTA's activity</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Events</p>
                    <p className="text-sm text-muted-foreground">Plan and manage events</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Wallet className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Treasurer</p>
                    <p className="text-sm text-muted-foreground">Track income and expenses</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Users className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Members</p>
                    <p className="text-sm text-muted-foreground">Manage your committee</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 4 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">4</div>
            <h2 className="text-xl font-bold">Set Up Your First Fundraising Goal</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Create a fundraising goal to track your progress:</p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to <strong className="text-foreground">Treasurer Dashboard</strong></li>
                <li>Navigate to the <strong className="text-foreground">Goals</strong> tab</li>
                <li>Click <strong className="text-foreground">Add Goal</strong></li>
                <li>Enter a name, target amount, and deadline</li>
                <li>Optionally make it visible on your public fundraising page</li>
              </ol>
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>You're Ready!</strong> You've completed the basic setup. Explore the platform and check out our other guides for more features.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    ),
  },

  "managing-member-roles": {
    title: "Managing Member Roles",
    category: "Administration",
    readTime: "6 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <Users className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Administration Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Learn how to assign and manage roles for your committee members. Proper role assignment ensures 
            everyone has the right level of access to perform their duties.
          </p>
        </div>

        {/* Available Roles */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Available Roles
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Badge className="bg-purple-500">Chair</Badge>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Full access to all features. Can manage members, events, finances, and settings.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Badge className="bg-green-500">Treasurer</Badge>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Access to financial features, income/expense tracking, and reports.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Badge className="bg-blue-500">Secretary</Badge>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Manage meetings, minutes, documents, and communications.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Badge className="bg-orange-500">Trustee</Badge>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">View access to governance documents and financial oversight.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Badge className="bg-gray-500">Member</Badge>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Basic access to view events, volunteer opportunities, and shared documents.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Assign Roles */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Assigning a Role</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Navigate to <strong className="text-foreground">Members</strong> from the main menu</li>
                <li>Find the member you want to update</li>
                <li>Click the <strong className="text-foreground">Edit</strong> button (pencil icon)</li>
                <li>Select their new role from the dropdown</li>
                <li>Click <strong className="text-foreground">Save Changes</strong></li>
              </ol>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Note:</strong> Only Chairs and PTA Admins can change member roles. Changes take effect immediately.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deputy Roles */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Using Deputy Roles</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Deputy roles allow members to have temporary or backup access to another role's permissions:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <UserCheck className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Assign a Deputy</p>
                    <p className="text-sm text-muted-foreground">In the member edit screen, select a deputy role to grant additional permissions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Common Use Cases</p>
                    <p className="text-sm text-muted-foreground">Vice-Chair as deputy for Chair, Assistant Treasurer as deputy for Treasurer</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Practices */}
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Best Practices
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Principle of Least Privilege</p>
                  <p className="text-sm text-muted-foreground">Only give members the permissions they need for their role</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Regular Reviews</p>
                  <p className="text-sm text-muted-foreground">Review roles annually or when committee members change</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Document Handovers</p>
                  <p className="text-sm text-muted-foreground">When roles change, ensure proper handover of responsibilities</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
  },

  "creating-your-first-event": {
    title: "Creating Your First Event",
    category: "Events",
    readTime: "7 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <Calendar className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Events Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Events are at the heart of PTA activities. Learn how to create, manage, and promote your events 
            using PTA Hub's comprehensive event management tools.
          </p>
        </div>

        {/* Step 1 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Create a New Event</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to the <strong className="text-foreground">Events Dashboard</strong></li>
                <li>Click <strong className="text-foreground">Add Event</strong></li>
                <li>Enter the event name (e.g., "Summer Fair 2024")</li>
                <li>Set the date and time</li>
                <li>Add a location and description</li>
              </ol>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> Use descriptive event names that parents will recognise.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 2 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Configure Event Settings</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Ticket className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Ticketed Events</p>
                    <p className="text-sm text-muted-foreground">Enable ticketing, set prices, and limit capacity</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Users className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Volunteer Sign-ups</p>
                    <p className="text-sm text-muted-foreground">Allow helpers to sign up for volunteer slots</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Stall Management</p>
                    <p className="text-sm text-muted-foreground">Manage external stall applications for fairs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Calendar className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Google Calendar Sync</p>
                    <p className="text-sm text-muted-foreground">Sync events to your committee's calendars</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Step 3 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">3</div>
            <h2 className="text-xl font-bold">Add Helpers & Volunteers</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Build your event team by assigning helpers:</p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Open your event and go to the <strong className="text-foreground">Helpers</strong> tab</li>
                <li>Click <strong className="text-foreground">Add Helper</strong></li>
                <li>Select a committee member or enter volunteer details</li>
                <li>Assign them a role or task (e.g., "Refreshments", "Set-up")</li>
                <li>Optionally share the volunteer sign-up link for external volunteers</li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Step 4 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">4</div>
            <h2 className="text-xl font-bold">Manage on the Day</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Play className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Event Check-in</p>
                    <p className="text-sm text-muted-foreground">Use the QR scanner to check in ticket holders</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Wallet className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Track Income</p>
                    <p className="text-sm text-muted-foreground">Record cash takings and sales during the event</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>After the Event:</strong> Don't forget to record final income and expenses, then archive the event!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    ),
  },

  "setting-up-fundraising-goals": {
    title: "Setting Up Fundraising Goals",
    category: "Fundraising",
    readTime: "5 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <Target className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Fundraising Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Set clear fundraising goals to motivate your community and track your progress. 
            Goals can be shared publicly to show parents what their contributions are working towards.
          </p>
        </div>

        {/* Why Set Goals */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Why Set Fundraising Goals?
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Eye className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Transparency</p>
                  <p className="text-sm text-muted-foreground">Show parents exactly what you're raising money for</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Users className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Motivation</p>
                  <p className="text-sm text-muted-foreground">Progress bars encourage more participation</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <BarChart3 className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Tracking</p>
                  <p className="text-sm text-muted-foreground">Monitor progress towards your targets</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Creating a Goal */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Create a New Goal</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to <strong className="text-foreground">Treasurer Dashboard</strong></li>
                <li>Navigate to the <strong className="text-foreground">Goals</strong> tab</li>
                <li>Click <strong className="text-foreground">Add Goal</strong></li>
                <li>Enter a descriptive title (e.g., "New Playground Equipment")</li>
                <li>Set your target amount</li>
                <li>Optionally add details explaining what the funds will be used for</li>
                <li>Set a target date if applicable</li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Public Display */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Share with Parents</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Make your goals visible to the school community:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Eye className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Public Fundraising Page</p>
                    <p className="text-sm text-muted-foreground">Toggle "Show on Fundraising Page" to make the goal visible publicly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Gift className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Accept Donations</p>
                    <p className="text-sm text-muted-foreground">Parents can donate directly towards specific goals via your fundraising page</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> Share your fundraising page link in school newsletters and social media!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tracking Progress */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">3</div>
            <h2 className="text-xl font-bold">Track Progress</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Keep your goals updated as funds come in:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Plus className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Manual Updates</p>
                    <p className="text-sm text-muted-foreground">Add contributions manually when recording event income</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Mark as Complete</p>
                    <p className="text-sm text-muted-foreground">Celebrate when you reach your target!</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    ),
  },

  "using-the-raffle-game": {
    title: "Using the Raffle Game",
    category: "Games",
    readTime: "6 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <Dices className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Games Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Run exciting digital raffles at your events! The raffle game provides a fun, fair, and 
            visually engaging way to draw winners for your prizes.
          </p>
        </div>

        {/* Features */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Raffle Features
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Ticket className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Custom Ticket Ranges</p>
                  <p className="text-sm text-muted-foreground">Set your own ticket number range to match your physical tickets</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Gift className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Multiple Prizes</p>
                  <p className="text-sm text-muted-foreground">Draw winners for multiple prizes in sequence</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Play className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Animated Draws</p>
                  <p className="text-sm text-muted-foreground">Exciting visual animation builds suspense</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Lock className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">No Duplicate Winners</p>
                  <p className="text-sm text-muted-foreground">System ensures each ticket can only win once</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Use */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Set Up Your Raffle</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to <strong className="text-foreground">Games</strong> from the main menu</li>
                <li>Select <strong className="text-foreground">Raffle</strong></li>
                <li>Enter the lowest ticket number sold</li>
                <li>Enter the highest ticket number sold</li>
                <li>Add your prize names (one per line)</li>
              </ol>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> List prizes in the order you want to draw them (e.g., 3rd place, 2nd place, 1st place for building suspense).
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Drawing Winners */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Draw Winners</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Click <strong className="text-foreground">Draw Winner</strong> to start the animation</li>
                <li>The winning ticket number will be revealed after the animation</li>
                <li>Announce the winner and award the prize</li>
                <li>Click <strong className="text-foreground">Draw Next</strong> for the next prize</li>
                <li>Continue until all prizes have been awarded</li>
              </ol>
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Display Tip:</strong> Connect to a projector or large screen so everyone can watch the draw!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Practices */}
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Tips for Success
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Test Before the Event</p>
                  <p className="text-sm text-muted-foreground">Run a practice draw to familiarise yourself with the system</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Keep Track of Tickets Sold</p>
                  <p className="text-sm text-muted-foreground">Make sure you know the exact range of tickets sold to avoid invalid draws</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Have a Backup Plan</p>
                  <p className="text-sm text-muted-foreground">Keep the device charged and have a mobile data backup in case of WiFi issues</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
  },

  "financial-reporting-guide": {
    title: "Financial Reporting Guide",
    category: "Finance",
    readTime: "8 min read",
    content: (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 border border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <BarChart3 className="h-8 w-8 text-primary" />
            <Badge variant="secondary">Finance Guide</Badge>
          </div>
          <p className="text-lg text-muted-foreground">
            Generate comprehensive financial reports for your committee meetings, AGMs, and charity returns. 
            PTA Hub makes it easy to track income, expenses, and produce professional reports.
          </p>
        </div>

        {/* Available Reports */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Available Reports
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <PieChart className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Income & Expenditure Report</p>
                  <p className="text-sm text-muted-foreground">Summary of all income and expenses for a period</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Calendar className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Event Financial Summary</p>
                  <p className="text-sm text-muted-foreground">Breakdown of income and costs per event</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Receipt className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Expense Claims Report</p>
                  <p className="text-sm text-muted-foreground">List of all expense claims and their status</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                <Target className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Goal Progress Report</p>
                  <p className="text-sm text-muted-foreground">Status of fundraising goals and contributions</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generating Reports */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">1</div>
            <h2 className="text-xl font-bold">Generate a Report</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to <strong className="text-foreground">Treasurer Dashboard</strong></li>
                <li>Navigate to the <strong className="text-foreground">Reports</strong> tab</li>
                <li>Select the report type you need</li>
                <li>Choose the date range or financial year</li>
                <li>Click <strong className="text-foreground">Generate Report</strong></li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Exporting */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">2</div>
            <h2 className="text-xl font-bold">Export Your Data</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Export reports in various formats:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <Download className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">PDF Export</p>
                    <p className="text-sm text-muted-foreground">Professional formatted reports for meetings and records</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <FileText className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">CSV Export</p>
                    <p className="text-sm text-muted-foreground">Raw data for spreadsheets and further analysis</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Tip:</strong> Export CSV files to import into your accounting software if needed.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Years */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">3</div>
            <h2 className="text-xl font-bold">Managing Financial Years</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p>Keep your finances organised by financial year:</p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li>Go to <strong className="text-foreground">PTA Admin → Settings</strong></li>
                <li>Find the <strong className="text-foreground">Financial Years</strong> section</li>
                <li>Create a new financial year at the start of your PTA year</li>
                <li>Set the start and end dates</li>
                <li>Archive previous years when complete</li>
              </ol>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm">
                  <strong>Important:</strong> Make sure all transactions are recorded before archiving a financial year.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Practices */}
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Best Practices
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Record Transactions Promptly</p>
                  <p className="text-sm text-muted-foreground">Enter income and expenses as soon as possible for accurate reporting</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Regular Reconciliation</p>
                  <p className="text-sm text-muted-foreground">Compare PTA Hub records with your bank statements monthly</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Keep Receipts</p>
                  <p className="text-sm text-muted-foreground">Upload receipt images when recording expenses for audit trails</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ArrowRight className="h-4 w-4 text-primary mt-1" />
                <div>
                  <p className="font-medium">Review Before Meetings</p>
                  <p className="text-sm text-muted-foreground">Generate reports before committee meetings to share with members</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
  },
};

export default function SupportArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;

  if (!article) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/support">
            <Button>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Support Centre
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/support">Support Centre</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{article.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{article.category}</Badge>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {article.readTime}
          </span>
        </div>
        <h1 className="text-3xl font-bold">{article.title}</h1>
      </div>

      {/* Content */}
      {article.content}

      {/* Back Button */}
      <div className="pt-6 border-t border-border">
        <Link to="/support">
          <Button variant="outline">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Support Centre
          </Button>
        </Link>
      </div>
    </div>
  );
}
